import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { UserService, DeletedUserResult } from "@/services/UserService";
import { useAuth } from "@/context/AuthContext";

interface RestoreUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestored?: (userId: number) => void;
}

const RestoreUserModal: React.FC<RestoreUserModalProps> = ({
  isOpen,
  onClose,
  onRestored,
}) => {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DeletedUserResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<DeletedUserResult | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2 || !user?.accesstoken) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      const data = await UserService.searchDeletedUsers(query.trim(), user.accesstoken);
      setResults(data);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, user?.accesstoken]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults([]);
      setSelectedUser(null);
      setError(null);
    }
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleRestore = useCallback(async () => {
    if (!selectedUser || !user?.accesstoken) return;

    setIsRestoring(true);
    setError(null);

    const result = await UserService.restoreUser(selectedUser.id, user.accesstoken);

    setIsRestoring(false);

    if (result.success) {
      toast.success(`User "${selectedUser.deletedName}" (ID: ${selectedUser.id}) restored`);
      onRestored?.(selectedUser.id);
      onClose();
    } else {
      setError(result.message || "Failed to restore user");
    }
  }, [selectedUser, user?.accesstoken, onRestored, onClose]);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "Unknown";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Unknown";
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <ArrowPathIcon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Restore Deleted User</h3>
              <p className="text-xs text-gray-500">Search by original name, email, or user ID</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Search input */}
          {!selectedUser && (
            <>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, email, or user ID..."
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  autoFocus
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Results */}
              {results.length > 0 && (
                <div className="space-y-2">
                  {results.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => setSelectedUser(u)}
                      className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-amber-300 hover:bg-amber-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{u.deletedName || "Unknown"}</div>
                          <div className="text-sm text-gray-500">{u.deletedUsername || "Unknown email"}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-400">ID: {u.id}</div>
                          <div className="text-xs text-gray-400">{u.sessionsCount} sessions</div>
                        </div>
                      </div>
                      {u.deletedAt && (
                        <div className="text-xs text-gray-400 mt-1">
                          Deleted: {formatDate(u.deletedAt)}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {query.trim().length >= 2 && !isSearching && results.length === 0 && (
                <div className="text-center text-gray-500 py-6 text-sm">
                  No deleted users found
                </div>
              )}

              {query.trim().length < 2 && (
                <div className="text-center text-gray-400 py-6 text-sm">
                  Type at least 2 characters to search
                </div>
              )}
            </>
          )}

          {/* Selected user confirmation */}
          {selectedUser && (
            <>
              <button
                onClick={() => { setSelectedUser(null); setError(null); }}
                className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
              >
                &larr; Back to search
              </button>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase">User to Restore</div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Name</span>
                    <span className="text-sm font-medium text-gray-900">{selectedUser.deletedName || "Unknown"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Email</span>
                    <span className="text-sm font-medium text-gray-900">{selectedUser.deletedUsername || "Unknown"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">User ID</span>
                    <span className="text-sm font-mono text-gray-900">{selectedUser.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Sessions</span>
                    <span className="text-sm font-medium text-gray-900">{selectedUser.sessionsCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Deleted</span>
                    <span className="text-sm text-gray-900">{formatDate(selectedUser.deletedAt)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  This will restore the user's original name and email. They will be able to log in again. Profile picture will need to be re-uploaded by the user.
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {selectedUser && (
          <div className="px-6 py-4 border-t flex gap-3">
            <button
              onClick={() => { setSelectedUser(null); setError(null); }}
              disabled={isRestoring}
              className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRestore}
              disabled={isRestoring}
              className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isRestoring && (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              Restore User
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default RestoreUserModal;
